import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import ShowcaseEntry from '@toasttab/shared/src/components/organisms/Navigation/Contentful/ShowcaseEntry'
import SubMenuEntry from '@toasttab/shared/src/components/organisms/Navigation/Contentful/SubMenuEntry'
import { useNavContext } from '@toasttab/shared/src/components/organisms/Navigation/Contentful/useNavContext'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { MenuProps } from '@toasttab/shared/src/components/organisms/Navigation/Contentful/ContentfulNavigation.d'
import DesktopTabbedMenu from './DesktopTabbedMenu'
import NavHighlightSection from '../NavHighlightSection'

const DesktopMenu = ({ dropdownContent, handleCloseNav, title }: MenuProps) => {
  const [isHidden, setIsHidden] = useState(true)
  const { headerHeight } = usePageContext()
  const { selectedParentMenu, setDesktopNavHeight } = useNavContext()

  const navMenuRef = useRef(null)

  useEffect(() => {
    if (title === selectedParentMenu) {
      setIsHidden(false)
    } else {
      setIsHidden(true)
    }
  }, [title, selectedParentMenu])

  useEffect(() => {
    if (!isHidden) {
      setDesktopNavHeight(navMenuRef?.current?.offsetHeight)
    }
  }, [isHidden, setDesktopNavHeight])

  const windowSize = useWindowSize()

  const handleMouseLeave = () => {
    // Keeps the menu open if the mouse moves up to the heading, closes it if they leave the nav item entirely
    if (title !== selectedParentMenu) {
      handleCloseNav()
    }
  }

  const menuClasses = classNames(
    'lg:flex transition-opacity duration-300 fixed w-full left-0 z-20',
    {
      // using visibility: hidden; over display: none; to allow the opacity transition to function
      'invisible opacity-0 top-[-9999px]': isHidden,
      'opacity-100 top-0': !isHidden
    }
  )
  const gridClasses = classNames(
    'grid gap-6 max-w-[1128px] w-full px-11 xl:px-0 mx-auto',
    {
      'py-10': !dropdownContent?.tabbedContent?.length,
      'grid-cols-4': !dropdownContent?.highlightSection,
      'grid-cols-12': dropdownContent?.highlightSection
    }
  )
  const showcaseClasses = classNames(
    'overflow-y-scroll !no-scrollbar p-[2px]',
    {
      'py-10': dropdownContent?.tabbedContent?.length,
      'col-span-3': dropdownContent?.highlightSection
    }
  )

  const submenusClasses = classNames(
    // p-[2px] to allow extra space for accessibility focus outlines
    'grid gap-x-6 gap-y-8 w-full rounded-br-lg overflow-y-scroll !no-scrollbar p-[2px]',
    {
      'col-span-3 grid-cols-3': !dropdownContent?.highlightSection,
      'col-span-5 grid-cols-2': dropdownContent?.highlightSection
    }
  )

  return (
    <nav
      aria-hidden={windowSize.width < 1024 || title !== selectedParentMenu}
      aria-label={`Toast ${title} Dropdown Menu`}
      className={menuClasses}
      data-testid='DesktopMenu'
      id={`${title.replace(/\s/g, '-')}-menu`}
      key={`desktop-sub-menu-${title}`}
      onMouseLeave={handleMouseLeave}
      ref={navMenuRef}
      style={{ top: !isHidden ? `${headerHeight}px` : null }}
    >
      <div className={gridClasses}>
        {dropdownContent?.highlightSection ? (
          <NavHighlightSection
            {...dropdownContent.highlightSection}
            classes='col-span-4'
          />
        ) : null}
        {dropdownContent?.tabbedContent ? (
          <DesktopTabbedMenu
            dropdownContent={dropdownContent}
            menuTitle={title}
          />
        ) : null}
        {dropdownContent?.showcaseEntries &&
        !dropdownContent?.highlightSection ? (
          // Display Showcase to the left of the submenu links unless we have the highlight section
          <div className={showcaseClasses}>
            {dropdownContent?.showcaseEntries.map((showcase: any) => (
              <ShowcaseEntry key={showcase.title} {...showcase} />
            ))}
          </div>
        ) : null}
        {dropdownContent?.subMenus ? (
          <div className={submenusClasses}>
            {dropdownContent?.subMenus?.columns?.map(
              (column: any, i: number) => (
                <div key={`desktop-sub-menu-column-${title}-${i}`}>
                  {column.map((submenu: any) => (
                    <SubMenuEntry
                      key={`desktop-sub-menu-section-${submenu.title}`}
                      {...submenu}
                    />
                  ))}
                  {column.columnLink ? (
                    <a
                      className='block p-4 text-[22px] mt-6 text-default font-normal flex items-center border-b border-light'
                      href={column.columnLink.url}
                      tabIndex={isHidden ? -1 : null}
                    >
                      <span className='before:content-icon-all-products before:block before:leading-none pt-0.5 bg-neutral rounded-full flex items-center justify-center w-8 h-8 mr-2' />
                      {column.columnLink.title}
                    </a>
                  ) : null}
                </div>
              )
            )}
          </div>
        ) : null}
        {dropdownContent?.showcaseEntries &&
        dropdownContent?.highlightSection ? (
          // Display Showcase to the right of the submenu links if we have the highlight section
          <div className={showcaseClasses}>
            {dropdownContent?.showcaseEntries.map((showcase: any) => (
              <ShowcaseEntry key={showcase.title} {...showcase} />
            ))}
          </div>
        ) : null}
      </div>
    </nav>
  )
}

export default DesktopMenu
